import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Eye, Copy, Trash2, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';

import FormBuilder from '@/components/FormBuilder';
import ScoringTemplateEditor from '@/components/ScoringTemplateEditor';

const AdminScholarshipModal = ({
  scholarshipDialog,
  setScholarshipDialog,
  scholarshipForm,
  setScholarshipForm,
  scholarships,
  committees,
  scholarshipTypes,
  editTab,
  setEditTab,
  saveScholarship,
  duplicateScholarship,
  addCriterion,
  updateCriterion,
  removeCriterion,
  descriptionRef,
  eligibilityRef,
  handleTextareaResize
}) => {
  return (
    <Dialog open={scholarshipDialog.open} onOpenChange={(open) => {
      // Only allow closing via Cancel or Save buttons, not by clicking outside
      if (!open) return;
      setScholarshipDialog({ ...scholarshipDialog, open });
    }}>
      <DialogContent 
        className="sm:max-w-3xl max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-heading">
            {scholarshipDialog.editing ? 'Edit Scholarship' : 'Create Scholarship'}
          </DialogTitle>
        </DialogHeader>
        
        <Tabs value={editTab} onValueChange={setEditTab}>
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="form">Form Fields</TabsTrigger>
            <TabsTrigger value="scoring">Scoring Template</TabsTrigger>
          </TabsList>
          
          {/* Details Tab */}
          <TabsContent value="details" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Scholarship Name *</Label>
                <Input
                  value={scholarshipForm.name}
                  onChange={(e) => setScholarshipForm({ ...scholarshipForm, name: e.target.value })}
                  className="rounded-sm"
                  data-testid="input-scholarship-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Amount ($) *</Label>
                <Input
                  type="number"
                  value={scholarshipForm.amount}
                  onChange={(e) => setScholarshipForm({ ...scholarshipForm, amount: Number(e.target.value) })}
                  className="rounded-sm"
                  data-testid="input-scholarship-amount"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                ref={descriptionRef}
                value={scholarshipForm.description}
                onChange={(e) => setScholarshipForm({ ...scholarshipForm, description: e.target.value })}
                onMouseUp={handleTextareaResize('description')}
                className="rounded-sm min-h-[80px]"
                style={{ resize: 'vertical' }}
                data-testid="input-scholarship-description"
              />
            </div>

            <div className="space-y-2">
              <Label>Eligibility Requirements *</Label>
              <Textarea
                ref={eligibilityRef}
                value={scholarshipForm.eligibility}
                onChange={(e) => setScholarshipForm({ ...scholarshipForm, eligibility: e.target.value })}
                onMouseUp={handleTextareaResize('eligibility')}
                className="rounded-sm min-h-[80px]"
                style={{ resize: 'vertical' }}
                data-testid="input-scholarship-eligibility"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={scholarshipForm.start_date}
                  onChange={(e) => setScholarshipForm({ ...scholarshipForm, start_date: e.target.value })}
                  className="rounded-sm"
                  data-testid="input-scholarship-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Deadline *</Label>
                <Input
                  type="datetime-local"
                  value={scholarshipForm.deadline}
                  onChange={(e) => setScholarshipForm({ ...scholarshipForm, deadline: e.target.value })}
                  className="rounded-sm"
                  data-testid="input-scholarship-deadline"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Scholarship Type *</Label>
              <Select
                value={scholarshipForm.scholarship_type}
                onValueChange={(value) => setScholarshipForm({ ...scholarshipForm, scholarship_type: value })}
              >
                <SelectTrigger className="rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {scholarshipTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Assign to Committee (Optional)</Label>
              <Select
                value={scholarshipForm.committee_id || 'unassigned'}
                onValueChange={(value) => setScholarshipForm({ 
                  ...scholarshipForm, 
                  committee_id: value === 'unassigned' ? null : value 
                })}
              >
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="Select a committee" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">No Committee (Direct Apply)</SelectItem>
                  {committees.map((committee) => (
                    <SelectItem key={committee.id} value={committee.id}>
                      {committee.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                If assigned, applicants must request access and be approved by the committee chair.
              </p>
            </div>

            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-sm border border-slate-200 mt-4">
              <div>
                <Label className="text-base">Previous Recipients</Label>
                <p className="text-sm text-slate-500">Can previous winners of this scholarship apply again?</p>
              </div>
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                <input
                  type="checkbox"
                  checked={scholarshipForm.allow_previous_recipients !== false}
                  onChange={(e) => setScholarshipForm({ ...scholarshipForm, allow_previous_recipients: e.target.checked })}
                  className="w-4 h-4 rounded border-slate-300 text-greek-blue focus:ring-greek-blue"
                  data-testid="input-allow-previous-recipients"
                />
                Allow Previous Recipients
              </label>
            </div>

            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-sm border border-slate-200 mt-4">
              <div>
                <Label className="text-base">Scholarship Status</Label>
                <p className="text-sm text-slate-500">Is this scholarship currently accepting applications?</p>
              </div>
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                <input
                  type="checkbox"
                  checked={scholarshipForm.is_active}
                  onChange={(e) => setScholarshipForm({ ...scholarshipForm, is_active: e.target.checked })}
                  className="w-4 h-4 rounded border-slate-300 text-greek-blue focus:ring-greek-blue"
                  data-testid="input-scholarship-active"
                />
                Active Status
              </label>
            </div>
          </TabsContent>

          {/* Scoring Template Tab */}
          <TabsContent value="scoring" className="space-y-4">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-slate-900 mb-1">Scoring Template</h3>
              <p className="text-sm text-slate-500">
                Define exactly how committee members will score applications. You can map specific fields from the form directly to scoring rules.
              </p>
            </div>
            
            <ScoringTemplateEditor
              template={scholarshipForm.scoring_template}
              formConfig={scholarshipForm.form_config}
              onChange={(newTemplate) => setScholarshipForm({ ...scholarshipForm, scoring_template: newTemplate })}
            />

            <div className="mt-8 pt-6 border-t border-slate-200">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="font-medium text-slate-900">Legacy Scoring Criteria</h3>
                  <p className="text-sm text-slate-500">Only used for older scholarships that haven't been migrated to the new scoring template system.</p>
                </div>
                <Button variant="outline" size="sm" onClick={addCriterion} className="rounded-sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Legacy Criterion
                </Button>
              </div>
              
              <div className="opacity-75 bg-slate-50 p-4 rounded-md border border-slate-200">
                {scholarshipForm.scoring_criteria.map((criterion, index) => (
                  <div key={index} className="grid grid-cols-12 gap-4 mb-4 items-end bg-white p-3 rounded-sm border border-slate-200">
                    <div className="col-span-4 space-y-2">
                      <Label>Criterion Name</Label>
                      <Input
                        value={criterion.name}
                        onChange={(e) => updateCriterion(index, 'name', e.target.value)}
                        className="rounded-sm"
                        data-testid={`input-criterion-name-${index}`}
                      />
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label>Weight</Label>
                      <Input
                        type="number"
                        step="0.05"
                        value={criterion.weight}
                        onChange={(e) => updateCriterion(index, 'weight', Number(e.target.value))}
                        className="rounded-sm"
                      />
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label>Max Score</Label>
                      <Input
                        type="number"
                        value={criterion.max_score}
                        onChange={(e) => updateCriterion(index, 'max_score', Number(e.target.value))}
                        className="rounded-sm"
                      />
                    </div>
                    <div className="col-span-3 space-y-2">
                      <Label>Description</Label>
                      <Input
                        value={criterion.description}
                        onChange={(e) => updateCriterion(index, 'description', e.target.value)}
                        className="rounded-sm"
                        data-testid={`input-criterion-desc-${index}`}
                      />
                    </div>
                    <div className="col-span-1 pb-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeCriterion(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-sm w-full"
                        data-testid={`remove-criterion-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Form Fields Tab */}
          <TabsContent value="form">
            <FormBuilder 
              formConfig={scholarshipForm.form_config}
              onChange={(newConfig) => setScholarshipForm({ ...scholarshipForm, form_config: newConfig })}
            />
          </TabsContent>
        </Tabs>
        <DialogFooter className="flex justify-between sm:justify-between">
          <div className="flex gap-2">
            {scholarshipDialog.editing && (
              <>
                <Link 
                  to={`/application/preview/${scholarshipDialog.editing}`}
                  target="_blank"
                >
                  <Button 
                    variant="outline" 
                    className="rounded-sm text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                    data-testid="preview-application-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Preview Application
                  </Button>
                </Link>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    const currentScholarship = scholarships.find(s => s.id === scholarshipDialog.editing);
                    if (currentScholarship) {
                      duplicateScholarship(currentScholarship);
                    }
                  }} 
                  className="rounded-sm text-blue-600 border-blue-300 hover:bg-blue-50"
                  data-testid="duplicate-from-edit-btn"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Duplicate
                </Button>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setScholarshipDialog({ open: false, editing: null })} className="rounded-sm">
              Cancel
            </Button>
            {scholarshipDialog.editing && (
              <Button 
                onClick={() => saveScholarship(false)} 
                variant="outline"
                className="rounded-sm border-greek-blue text-greek-blue hover:bg-greek-blue/10" 
                data-testid="save-continue-btn"
              >
                Save Scholarship
              </Button>
            )}
            <Button onClick={() => saveScholarship(true)} className="bg-greek-blue hover:bg-greek-blue-dark text-white rounded-sm" data-testid="save-scholarship-btn">
              {scholarshipDialog.editing ? 'Save & Close' : 'Create Scholarship'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminScholarshipModal;